const DB_NAME = 'CasePrepBuddyDB';
const DB_VERSION = 1;
const STORE_NAME = 'prepRuns';
const LAST_RUN_KEY = 'casePrepBuddy:lastRunId';
const FALLBACK_RUNS_KEY = 'casePrepBuddy:runs';

function canUseBrowserStorage() {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function canUseIndexedDb() {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

function cloneSerializable(value) {
  if (typeof structuredClone === 'function') {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value));
}

function createRandomSegment() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(6);
    crypto.getRandomValues(bytes);
    return Array.from(bytes)
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  return Math.random().toString(16).slice(2, 14);
}

export function createClientId(prefix = 'cpb') {
  return `${prefix}_${Date.now()}_${createRandomSegment()}`;
}

function safeLocalStorageGet(key) {
  if (!canUseBrowserStorage()) return null;

  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    console.debug('casePrepBuddyStorage: localStorage get failed', error);
    return null;
  }
}

function safeLocalStorageSet(key, value) {
  if (!canUseBrowserStorage()) return;

  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    console.debug('casePrepBuddyStorage: localStorage set failed', error);
  }
}

function safeLocalStorageRemove(key) {
  if (!canUseBrowserStorage()) return;

  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    console.debug('casePrepBuddyStorage: localStorage remove failed', error);
  }
}

function getFallbackRuns() {
  const raw = safeLocalStorageGet(FALLBACK_RUNS_KEY);

  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.debug('casePrepBuddyStorage: fallback JSON parse failed', error);
    return [];
  }
}

function setFallbackRuns(runs) {
  safeLocalStorageSet(FALLBACK_RUNS_KEY, JSON.stringify(runs));
}

function openDatabase() {
  return new Promise((resolve, reject) => {
    if (!canUseIndexedDb()) {
      reject(new Error('IndexedDB is unavailable in this browser context.'));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      let store;

      if (!db.objectStoreNames.contains(STORE_NAME)) {
        store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      } else {
        store = event.target.transaction.objectStore(STORE_NAME);
      }

      if (!store.indexNames.contains('updatedAt')) {
        store.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error('Failed to open Case Prep Buddy database.'));
  });
}

function transactionDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error || new Error('IndexedDB transaction failed.'));
    tx.onabort = () => reject(tx.error || new Error('IndexedDB transaction was aborted.'));
  });
}

async function saveFallbackRun(run) {
  const runs = getFallbackRuns();
  const nextRun = cloneSerializable(run);
  const existingIndex = runs.findIndex((entry) => entry.id === nextRun.id);

  if (existingIndex >= 0) {
    runs.splice(existingIndex, 1, nextRun);
  } else {
    runs.push(nextRun);
  }

  setFallbackRuns(runs);
  safeLocalStorageSet(LAST_RUN_KEY, nextRun.id);
  return nextRun;
}

async function loadFallbackRun(id) {
  const runs = getFallbackRuns();
  return runs.find((run) => run.id === id) || null;
}

export function getLastPrepRunId() {
  return safeLocalStorageGet(LAST_RUN_KEY);
}

export function clearLastPrepRunPointer() {
  safeLocalStorageRemove(LAST_RUN_KEY);
}

export function createEmptyPrepRun() {
  const timestamp = new Date().toISOString();

  return {
    id: createClientId('prepRun'),
    createdAt: timestamp,
    updatedAt: timestamp,
    status: 'draft',
    draftTitle: '',
    sourceItems: [],
    stagedTransactions: [],
    suggestedLabels: [],
    notes: ''
  };
}

export function touchPrepRun(run) {
  return {
    ...cloneSerializable(run),
    updatedAt: new Date().toISOString()
  };
}

export async function savePrepRun(run) {
  const nextRun = touchPrepRun(run);

  if (!canUseIndexedDb()) {
    return saveFallbackRun(nextRun);
  }

  try {
    const db = await openDatabase();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(nextRun);
    await transactionDone(tx);
    db.close();
    safeLocalStorageSet(LAST_RUN_KEY, nextRun.id);
    return nextRun;
  } catch (error) {
    console.debug('casePrepBuddyStorage: falling back to localStorage after save failure', error);
    return saveFallbackRun(nextRun);
  }
}

export async function loadPrepRun(id) {
  if (!id) return null;

  if (!canUseIndexedDb()) {
    return loadFallbackRun(id);
  }

  try {
    const db = await openDatabase();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).get(id);

    const result = await new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error || new Error('Failed to load prep run.'));
    });

    db.close();
    return result;
  } catch (error) {
    console.debug('casePrepBuddyStorage: falling back to localStorage after load failure', error);
    return loadFallbackRun(id);
  }
}

export async function loadLastPrepRun() {
  return loadPrepRun(getLastPrepRunId());
}

export async function listPrepRuns() {
  if (!canUseIndexedDb()) {
    return getFallbackRuns().sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
  }

  try {
    const db = await openDatabase();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).getAll();

    const result = await new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(Array.isArray(request.result) ? request.result : []);
      request.onerror = () => reject(request.error || new Error('Failed to list prep runs.'));
    });

    db.close();
    return result.sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
  } catch (error) {
    console.debug('casePrepBuddyStorage: falling back to localStorage after list failure', error);
    return getFallbackRuns().sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)));
  }
}

export async function deletePrepRun(id) {
  if (!id) return;

  if (!canUseIndexedDb()) {
    const runs = getFallbackRuns().filter((run) => run.id !== id);
    setFallbackRuns(runs);
    if (getLastPrepRunId() === id) {
      clearLastPrepRunPointer();
    }
    return;
  }

  try {
    const db = await openDatabase();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    await transactionDone(tx);
    db.close();
  } catch (error) {
    console.debug('casePrepBuddyStorage: falling back to localStorage after delete failure', error);
    const runs = getFallbackRuns().filter((run) => run.id !== id);
    setFallbackRuns(runs);
  }

  if (getLastPrepRunId() === id) {
    clearLastPrepRunPointer();
  }
}
